/**
 * PATH TO GO — mandatory report gate.
 *
 * The "PATH TO GO" wizard codifies an EXPLICIT required-report set: the
 * concrete deliverables a site MUST have before a GO decision can be asserted.
 * This is the contract the wizard's Generate stage (what to run next) and the
 * Decision stage (is the GO gate satisfied?) both read.
 *
 * REUSE-FIRST: the relevance rules mirror `intentCriticalScope(intent)`
 * (computeOverviewFindingCards) — the platform's single source of truth for
 * "which report types does THIS declared intent make critical". We do NOT
 * re-derive intent signals; we read the SAME `ProjectIntent` fields
 * `intentCriticalScope` reads and apply a SUPERSET-consistent rule.
 *
 * THE RULE (documented + falsifiable):
 *   - feasibility            — ALWAYS required (the base go/no-go diligence).
 *   - power-availability     — NO LONGER always required. Stage 5.1 moved the
 *                              grid-SCREENING half into BTM Feasibility, and
 *                              Stage 5.2 gated the remainder (the Generation &
 *                              Impact report) on a PROMOTED generation
 *                              scenario. Firm-capacity adequacy is still the
 *                              spine of a BTM decision, but a grid-only site
 *                              never promotes a scenario and gets that grid
 *                              analysis through feasibility instead — so
 *                              requiring it of every site would make the GO
 *                              gate unreachable by construction for the most
 *                              common site type.
 *   - environmental          — required when the intent signals a
 *                              sustainability / emissions priority
 *                              (`intent.priority === 'sustainability'`), mirroring
 *                              `intentCriticalScope`'s `priority==='sustainability'
 *                              → environmental` branch.
 *   - water-resource         — ALWAYS required (2026-09-05, P1.2). The second
 *                              unconditional domain of the diligence floor —
 *                              see `ALWAYS_REQUIRED_FOR_GO` for the arithmetic
 *                              that forced a second domain and the reasons
 *                              water was the one chosen. The water-withdrawal
 *                              signal (`intentSignalsWaterResource`: a
 *                              water-withdrawing `bopHeatRejection` sink, or
 *                              liquid/hybrid rack cooling as a lower-confidence
 *                              fallback) is RETAINED as the relevance model's
 *                              statement of when water is a decision-DRIVING
 *                              question; it no longer decides membership.
 *   - financial              — required when the intent carries COMMERCIAL /
 *                              FINANCING maturity AND the saved financial model
 *                              passes the canonical minimum-input readiness gate.
 *   - interconnection        — CONNECTIVITY-ADVISORY (2026-08-25): currently
 *                              NEVER required (`CONNECTIVITY_GATES_GO` is
 *                              false — connectivity data coverage is too thin
 *                              to gate a decision). When the flag is flipped
 *                              back on, required when the intent makes the
 *                              connectivity posture decision-critical
 *                              (`intentSignalsConnectivityDiligence`).
 *   - supply-chain-risk      — required when the intent carries PROCUREMENT
 *                              exposure AND a reportable user-authored/uploaded
 *                              equipment BOM establishes the chosen design basis.
 *   - public-sentiment       — NEVER required (owner, 2026-09-30: an
 *                              ADDITIONAL analysis, outside both stages). It
 *                              used to be required when the intent declared
 *                              COMMUNITY exposure (`intentSignalsCommunityExposure`,
 *                              retained as the relevance statement). A present
 *                              report still feeds the social pillar and, like
 *                              any present non-mandatory source, caps a red
 *                              reading at CONDITIONAL (Stage 1.5).
 *
 * Every conditional rule above is signal-GATED on a DECLARED intent value. In
 * addition, model-dependent domains are authority-gated: commercial intent
 * without a complete model and procurement intent without a valid BOM remain
 * available-later work, never missing decision evidence.
 *
 * REQUIRED ≠ ABLE TO KILL. Requirement and floor authority are separate axes:
 * `sourceFloorPolicy` decides how far a BAD reading from each source may push
 * the band. The three sources above are MITIGABLE — they cap the call at
 * CONDITIONAL and can never assert NO-GO on their own, because each names a
 * problem that time, capital, routing, or engagement can address. Physics,
 * permitting and financeability sources stay 'hard'.
 *
 * The ordered list always places SOURCE (primary-diligence) reports before the
 * executive-summary synthesis, because a GO decision's exec summary is
 * meaningless until its source reports exist.
 *
 * Pure / deterministic — same intent → same set.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type {
  PowerAvailabilityPreferences,
  ProjectIntent,
} from '../../types/siteRag';
import { WATER_WITHDRAWING_SINKS } from '../reportGeneration/reviewScoring/extractCrossFieldFindings';
import { requiresGenerationImpactTestFit } from '../generationImpact/testFitApplicability';
import { resolveUltimateCampusMW } from '../projectIntent';
import { resolveAnalysisDecisionMateriality } from './analysisApplicability';

type RequirementAuthoritySite = Partial<SiteEntity>;

/**
 * Canonical ordering of the GO-gate report set. SOURCE reports first (in the
 * order they should generally be produced), executive-summary last. Any report
 * type added to the required set is emitted in this order so the wizard renders
 * a stable, sensible sequence.
 */
const GO_REPORT_ORDER: readonly ReportTypeId[] = [
  'feasibility',
  'power-availability',
  'test-fit',
  // Connectivity Feasibility Study. Placed after the physical siting reports
  // and before the environmental/water permitting axis: connectivity decides
  // whether the site can BE a data center, and its queue-status signal shares
  // the energization timeline the power report establishes. Intent-gated (see
  // intentSignalsConnectivityDiligence) — NOT in ALWAYS_REQUIRED_FOR_GO.
  'interconnection',
  'environmental',
  'water-resource',
  // Supply Chain Risk. After the technical scope is set (it prices the
  // long-lead equipment THAT scope implies) and before the financial report
  // that consumes its CAPEX/COD exposure. Intent-gated.
  'supply-chain-risk',
  // Public Sentiment. Last of the source reports: its themes (noise, emissions,
  // water, fuel logistics) are only assessable once the build those themes
  // concern is defined by the reports above. Intent-gated.
  'public-sentiment',
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. Placed AFTER the
  // power/water source reports (its economics read their firm-capacity / cost
  // basis) and BEFORE the executive-summary synthesis (the financial verdict
  // feeds the GO rollup). Intent-gated into the required set (see
  // intentSignalsFinancialDiligence) — NOT in ALWAYS_REQUIRED_FOR_GO.
  'financial',
  'executive-summary',
] as const;

/**
 * Report types that are ALWAYS in the GO gate regardless of intent — the
 * irreducible diligence floor.
 *
 * Power Availability LEFT this set (2026-09-03). Its spine is firm-capacity
 * adequacy measured against the declared facility load, so before that load
 * exists the report has no denominator and its grid-vs-BTM and LCOE sections
 * assert a technology and cost conclusion with nothing to size them against.
 * It is now authority-gated on the declared load, the same intent+authority
 * shape already used for Financial and Supply Chain, and is added to the
 * required set the moment the load is declared (see below).
 *
 * The early grid question is NOT lost by this: Feasibility carries `gridStudy`
 * and requires the same grid/electricity SED sections, so a site can still be
 * screened for grid headroom from day one.
 *
 * WATER RESOURCE JOINED this set (2026-09-05, P1.2) — a product decision,
 * recorded here because it is a claim about what diligence the platform
 * insists on before it will speak.
 *
 * WHY A SECOND DOMAIN AT ALL. `MIN_DECISION_WEIGHT_COVERAGE` (0.65) is an
 * EVIDENCE floor over the nine non-advisory pillars (0.90 of registry weight).
 * One feasibility report measures power 0.21 + land 0.05 + regulatory 0.10 +
 * financial 0.15 + evidenceQuality 0.03 = 0.54, i.e. 0.54 / 0.90 = 0.60 — and
 * `power-availability`, `test-fit` and `financial` add +0.00 because they feed
 * only pillars feasibility already measures. So with feasibility as the only
 * unconditional report, a minimal intent (grid-only, air-cooled, no priority,
 * which fires none of the intent signals below) was COMPLETE by the requirement
 * gate and INADEQUATE by the evidence gate, with no required work left to do:
 * GO was unreachable by construction for the most ordinary site shape. The
 * floor is not wrong (0.65 sits in a genuine gap of the reachable ladder —
 * nothing lands between 0.600 and 0.689); the required set was short by one
 * domain that a minimal site can always produce. Requiring MORE evidence is
 * the only acceptable fix — the floor is never lowered to make sites bandable.
 *
 * WHY WATER, of the four domains that clear the gap:
 *   - `environmental` (+0.08 → 0.689) requires the `airQuality` SED slot at a
 *     ONE-DAY TTL. A live AQI refresh moves that slot's fingerprint, and
 *     `reportSedIsCurrent` reads a generated environmental report as
 *     non-current almost immediately, so PATH 4's completeness cap would
 *     re-fire on every site permanently — a CONDITIONAL no amount of work can
 *     clear. It is also blocked inside the US whenever the AirNow key is
 *     rate-limited.
 *   - `public-sentiment` (+0.10, already mitigable) always generates, but on a
 *     quiet site its barren fallback deliberately emits prose matching none of
 *     `evaluateSocial`'s patterns, so the pillar composes `missing` and lifts
 *     coverage by exactly nothing: GO stays at 0.600. It is also by far the
 *     most expensive report to run.
 *   - `supply-chain-risk` (+0.08, mitigable) is authority-gated on a reportable
 *     BOM, so it cannot be produced for every site — the grid-only defect class
 *     (P1.3) again.
 *   - `water-resource` (+0.10 → 0.711) has the most stable SED slots of the
 *     four (waterStress 90 d, climate/environmental/regulatory 30 d, no daily
 *     slot), scores its pillar from TYPED global provider data (`evaluateWater`
 *     reads WRI Aqueduct `waterStress.data.stressLevel` at weight 1.5, not
 *     prose), and is the cheapest of the four to generate. `intentCriticalScope`
 *     now lists it for every site (it used to add water only for liquid /
 *     hybrid cooling or a drought-exposed source), so this module's superset
 *     invariant holds. Its providers (`wri-aqueduct`, `open-meteo`, `usfws-env`)
 *     add no new class of ungeneratable TERRITORY: `usfws-env` is US-only, and
 *     so are `hifld-grid` / `eia-electricity`, which feasibility — already
 *     required — depends on. A transient `usfws-env` outage is a different
 *     matter, so water carries the same `fema-flood` fallback environmental
 *     does (`providerCoverageRegistry.ts`).
 *
 * STATED CONSEQUENCES, deliberately accepted:
 *   1. The platform now requires water diligence of sites whose declared intent
 *      (dry sink, air-cooled) says water is not a decision-driving question.
 *      That is inherent to ANY universal requirement and cannot be engineered
 *      away; the position taken is that a data centre's water exposure —
 *      basin stress, rights, drought, permits — is baseline diligence whatever
 *      the cooling choice, in the same way feasibility is.
 *   2. Promotion changes floor authority. Post Stage 1.5, a NON-mandatory red
 *      water report capped the call at CONDITIONAL; a MANDATORY red under
 *      water's 'hard' policy floors it to NO-GO. 'hard' is kept: "the water
 *      isn't there" is this file's own canonical hard finding, and unlike
 *      environmental — whose required output is a priced, timed mitigation
 *      path — water absence is a property of the site. The narrative route to
 *      that red is closed instead: `water-resource` is added to
 *      `VERDICT_LEAN_EXEMPT_REPORT_TYPES` (reportVerdict.ts) so an LLM lean can
 *      no longer push an amber water verdict to red. A verdict that can decline
 *      a site must be deterministically earned.
 *   3. The composite now carries the water pillar for every site with a water
 *      report, so a dry-cooled site in a stressed basin reads its basin stress
 *      in its composite. Directionally conservative; recorded so it is not
 *      mistaken for a scoring bug. If this proves too blunt, the lever is an
 *      intent-aware floor policy for water, NOT removing it from this set.
 *   4. Scorer semantics changed (the required set feeds `computeSourceFloor`
 *      inside `computeSiteComposite`), so `COMPOSITE_INPUTS_FINGERPRINT_VERSION`
 *      moved fp16 → fp17 and every persisted composite recomputes.
 *
 * NOT done, on evidence: a dynamic "required = whatever clears the floor" rule.
 * It is self-cancelling (satisfying the requirement deletes it and downgrades
 * the report's authority), makes NO-GO authority anti-monotone (byte-identical
 * red reports, opposite verdicts, depending on which UNRELATED reports exist)
 * and violates this module's purity and superset invariants, both pinned by
 * test. A fixed addition is checkable against the invariant in one reading.
 *
 * Exported so the applicability labeller and its tests can pin the mirror
 * (`analysisApplicability.ts` cannot import this module — it would be a cycle).
 */
export const ALWAYS_REQUIRED_FOR_GO: readonly ReportTypeId[] = [
  'feasibility',
  // TWO-STAGE PIPELINE, Phase 3 part B (2026-09-07; owner decisions D1 + D3):
  // `water-resource` LEFT this set — the type is RETIRED. Its screen (supply
  // availability, groundwater & surface water, water rights, drought
  // resilience) is now a chapter of the feasibility report (`waterAnalysis`),
  // and the scenario's water DUTY rides on Generation & Impact (P3.2). The
  // water pillar reads those two sources (`pillarRegistry.water`), so the
  // floor arithmetic above is re-pinned: feasibility measures power 0.21 +
  // financial 0.15 + regulatory 0.10 + land 0.05 + evidenceQuality 0.03 +
  // WATER 0.10 = 0.64; with environmental (+0.08) the Stage 1 floor measures
  // 0.72 / 0.90 = 0.80 of decision weight against the 0.65 gate — more than
  // the 0.711 the standalone water report gave, from one report fewer.
  // Consequence 2 above (a mandatory red water report DECLINES a site) is now
  // carried by feasibility's hard floor for an explicit withdrawal-permit
  // denial in its water screen. Other water findings feed the water pillar;
  // they do not independently become a hard floor. Existing water reports remain
  // readable and, while current, still cap as NON-mandatory sources.
  // TWO-STAGE PIPELINE, Phase 3 (2026-09-06; owner decisions D1 + D2):
  //   - `environmental` joins the floor for every site — in Stage 1 · Early
  //     screening then; Stage 2 · Mature validation since 2026-09-30 (owner:
  //     "it requires equipment"), locked until a generation scenario is
  //     promoted unless the site declared grid-only (`stageLockFor`). Still
  //     required, so the floor arithmetic above is unchanged; only the stage
  //     that asks for it moved. The one-day `airQuality` TTL that kept it out (above) no longer
  //     bites: Phase 2 exempted `airQuality` from environmental's
  //     currency-critical slots (`REPORT_CURRENCY_VOLATILE_SED_SECTIONS`), so a
  //     generated environmental report stays current until a durable slot
  //     moves. Its red is MITIGABLE (`MITIGABLE_FLOOR_SOURCES`): the report's
  //     required output is a priced, timed mitigation path, so it caps at
  //     CONDITIONAL and never declines a site on its own. Its narrative lean is
  //     also closed (`VERDICT_LEAN_EXEMPT_REPORT_TYPES`).
  //   - `financial` joins the Stage 2 · Mature validation floor for every
  //     site. A data centre cannot be called a PROCEED with its economics never
  //     modelled; the report stays authority-gated on the saved financial
  //     model to RUN (the plan lanes it "Complete the financial model first"),
  //     so the decision waits for the model rather than skipping the question.
  //     Its red keeps the 'hard' policy: an uneconomic project is declined.
  //   - `supply-chain-risk` is required in Stage 2 whenever on-site generation
  //     applies (see `requiredReportsForGoUnderPolicy`); a grid-only site has
  //     no generation equipment to source, so it is not asked for one.
  // Direction: GO gets HARDER (more required evidence), never easier. Scorer
  // semantics changed (the required set feeds `resolveTwoStageHold` and
  // `computeSourceFloor`), so `COMPOSITE_INPUTS_FINGERPRINT_VERSION` moved
  // fp18 → fp19 and every persisted composite recomputes.
  'environmental',
  'financial',
] as const;

/**
 * CONNECTIVITY-ADVISORY (2026-08-25) — does the Connectivity Feasibility Study
 * participate in the GO/NO-GO calculation at all?
 *
 * FALSE for now, by decision: the connectivity evidence base is a single
 * provider (PeeringDB) exposing five fields — nearest-IX name/city/distance,
 * IX count and colo-facility count within 50 km — with no carrier list, no
 * lit/dark fiber data, no measured latency (the model is a distance×0.05 ms
 * proxy), and a silent colo-for-IX substitution failure mode (the `usedIx`
 * fallback branch in `dataProviders/peeringDb.ts`: when the IX list fetch
 * fails, the nearest colo facility is emitted in the `nearestIx*` fields —
 * previously disclosed only in the prose source string). That is not enough
 * data to let connectivity
 * move an investment-grade verdict, so it is an OPTIONAL, ADVISORY analysis:
 * still generatable, still displayed, offered as a Path-to-GO side quest, and
 * surfaced on the decision guide as an advisory row — but it neither raises
 * nor lowers the band.
 *
 * This constant is the REQUIREMENT half of the demotion; the composite half is
 * `advisory: true` on the `interconnection` pillar in
 * `src/lib/portfolio/pillarRegistry.ts`. The two halves MUST flip together —
 * a pillar in the composite whose report is not in the required set sits in a
 * dead zone (raises the band, never lowers it).
 *
 * RE-PROMOTION CHECKLIST — flip to `true` (and drop the pillar's `advisory`
 * flag) only when ALL of the following hold:
 *   1. ≥2 independent connectivity providers (e.g. PeeringDB + FCC BDC fabric
 *      or a commercial carrier dataset) agree within tolerance on IX distance
 *      and facility counts — no single-API silent failure mode.
 *   2. The PeeringDB colo-substitution fallback is eliminated at the provider
 *      (`nearestIxKind === 'ixp'` invariant; a colo is never emitted in the
 *      `nearestIx*` fields). HARD precondition — an IX-list outage must not
 *      be able to publish a colo distance as an IX distance at all, rather
 *      than relying on every downstream consumer to honour the label.
 *   3. Carrier-level evidence exists: named carriers and lit/dark fiber
 *      presence within a defined radius in `ConnectivityEvidence`.
 *   4. Latency is measured or carrier-quoted, replacing the distance proxy in
 *      `src/lib/ixLatency.ts`.
 *   5. The missing-data honesty tests still hold (absent data is `missing`,
 *      never scored).
 *   6. The lerp anchors in `scoringThresholds.json` are recalibrated against
 *      the richer dataset.
 *   7. A replay-harness impact study over a benchmark site set has quantified
 *      the band movement the flip causes.
 *
 * Tests exercise BOTH values of this policy via
 * `requiredReportsForGoUnderPolicy` (the demotion and re-promotion branches
 * are both live code paths), so this is a tested policy seam, not dormant
 * code. Typed `boolean` (not the literal) so the gated branch is not
 * narrowed to unreachable.
 */
export const CONNECTIVITY_GATES_GO: boolean = false;

// ── Per-source floor policy ──────────────────────────────────────────────────

/**
 * How far down a single BAD source verdict may push the overall Go/No-Go call
 * (`executiveDecisionRollup` PATH 2).
 *
 * Before this policy the floor was UNIFORM — any red mandatory source forced
 * NO-GO. That was defensible while the gate held only physics reports, but it
 * does not survive contact with a wider source set: a single "Opposed" public
 * sentiment reading would have hard-killed an otherwise sound site, and the
 * rational response to that would be to stop generating the report — exactly
 * the wrong incentive.
 *
 * The split is about REVERSIBILITY, not importance:
 *
 *   - 'hard'      — the finding is a property of the site or the money. No
 *                   amount of schedule or engagement changes it: the water
 *                   isn't there, the grid can't carry it, the permit is barred,
 *                   the project doesn't finance. These may floor to NO-GO.
 *   - 'mitigable' — the finding is real and may be expensive, but it is
 *                   addressable with time, capital, routing, or engagement.
 *                   These cap at CONDITIONAL and can never assert NO-GO.
 *
 * A 'mitigable' source is NOT a soft source — CONDITIONAL still blocks an
 * unconditional GO, still emits a binding constraint, and still surfaces in the
 * Resolve stage. It simply cannot be the sole author of a NO-GO.
 */
export type SourceFloorPolicy = 'hard' | 'mitigable';

/**
 * Sources whose bad verdict is addressable rather than disqualifying.
 *
 *   - interconnection    — fiber routes can be built and carriers contracted;
 *                          a thin connectivity posture is a cost and schedule
 *                          problem, not a site-viability one. RETAINED while
 *                          `CONNECTIVITY_GATES_GO` is false (the type is never
 *                          required, so this entry is currently unreachable in
 *                          production): if connectivity is ever re-gated it
 *                          must come back MITIGABLE — `sourceFloorPolicy`
 *                          fails closed to 'hard' for unlisted types.
 *   - supply-chain-risk  — procurement exposure is a COD-schedule risk. Long
 *                          lead times slip a project; they do not disqualify a
 *                          site.
 *   - public-sentiment   — opposition is real and can kill projects, but it is
 *                          the output of a process that engagement, mitigation,
 *                          and design changes can move. Treating a sentiment
 *                          index as an automatic NO-GO would also hand a single
 *                          LLM-derived index veto authority over the whole call.
 */
const MITIGABLE_FLOOR_SOURCES: readonly ReportTypeId[] = [
  'interconnection',
  'supply-chain-risk',
  'public-sentiment',
  // D1 (two-stage, 2026-09-06): environmental is always required, and its
  // required output is a mitigation pathway — a red caps at CONDITIONAL.
  'environmental',
] as const;

/**
 * Resolve how far a bad verdict from `type` may push the overall band.
 * Unknown / unlisted types default to 'hard' — fail-closed, so a newly added
 * decision source is conservative until someone deliberately classifies it.
 */
export function sourceFloorPolicy(type: ReportTypeId): SourceFloorPolicy {
  return MITIGABLE_FLOOR_SOURCES.includes(type) ? 'mitigable' : 'hard';
}

/**
 * Does the declared intent signal a sustainability / emissions priority?
 * Mirrors `intentCriticalScope`'s `intent.priority === 'sustainability'` branch
 * (which adds `environmental`). Kept as a named predicate so the rule is
 * falsifiable and grep-able.
 */
function intentSignalsEnvironmental(intent: ProjectIntent | undefined): boolean {
  return intent?.priority === 'sustainability';
}

/**
 * Does the declared intent draw cooling water? Water WITHDRAWAL is driven by the
 * cold-END heat-rejection SINK (`bopHeatRejection`), NOT the orthogonal
 * rack-side `coolingStrategy` (air vs liquid). This signal reads the
 * SAME `WATER_WITHDRAWING_SINKS` set the cross-field water-conflict guard uses
 * (`extractCrossFieldFindings.ts` / `siteRagService.ts`), so the signal, the
 * guard, and the recommendation can never drift.
 *
 * P1.2 (2026-09-05): this predicate NO LONGER decides required-set membership —
 * water was made unconditional then; Phase 3 part B (2026-09-07) retired the
 * standalone type and folded its screen into feasibility. It is kept,
 * and exported, as the relevance model's statement of when water is a
 * decision-DRIVING question (it mirrors `intentCriticalScope`'s water branch
 * and stays falsifiable by test); surfaces that rank or explain the water
 * analysis may read it, the GO gate does not.
 *
 * Decision (mirrors the guard):
 *   - `bopHeatRejection` SET → authoritative. A water-withdrawing sink
 *     (wet tower / once-through / hybrid) IS gated; any DRY sink (dry-cooler /
 *     free-cooling / absorption-cchp / unknown) is NOT — even under liquid-rack
 *     cooling (a liquid-rack + dry-cooled site withdraws ~no water).
 *   - `bopHeatRejection` UNSET → LOWER-CONFIDENCE FALLBACK on the rack-side
 *     `coolingStrategy === 'liquid' | 'hybrid'` (a wet sink could still be
 *     chosen; mirrors the guard's warn branch). An air-rack site with no sink
 *     declared is NOT gated.
 *
 * `undecided` / absent on BOTH axes → NOT water-required for the GO gate (the
 * gate only hard-requires reports the declared intent makes unambiguously
 * relevant).
 */
export function intentSignalsWaterResource(intent: ProjectIntent | undefined): boolean {
  const sink = intent?.bopHeatRejection;
  if (sink != null) {
    // Authoritative cold-end signal — gate iff the sink withdraws water.
    return WATER_WITHDRAWING_SINKS.has(sink);
  }
  // Fallback: no declared sink → use the rack-side cooling as a lower-confidence
  // proxy (a liquid/hybrid loop could yet be paired with a wet sink).
  return intent?.coolingStrategy === 'liquid' || intent?.coolingStrategy === 'hybrid';
}

/**
 * FIN-1 (wf w7nuyquwd) — does the declared intent signal that a FINANCIAL
 * viability call is part of the GO decision? The financial report is gated to
 * intents that carry COMMERCIAL / FINANCING maturity (so a planning-stage site
 * with no economics declared is not forced to produce a financial report):
 *
 *   - `priority === 'cost'`  — cost/economics is the declared decision driver.
 *   - a declared `offtake.structure` (merchant / contracted / hybrid) — the
 *     operator has a revenue posture to value.
 *   - a declared PPA price (`offtake.pricePerMwhUsd > 0`) — concrete revenue.
 *
 * Kept as a named, falsifiable, grep-able predicate (mirrors the other
 * intent-signal helpers above). `undecided` / absent → NOT financial-required.
 */
export function intentSignalsFinancialDiligence(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  if (intent.priority === 'cost') return true;
  const offtake = intent.offtake;
  if (offtake?.structure === 'merchant' || offtake?.structure === 'contracted' || offtake?.structure === 'hybrid') {
    return true;
  }
  if (typeof offtake?.pricePerMwhUsd === 'number' && Number.isFinite(offtake.pricePerMwhUsd) && offtake.pricePerMwhUsd > 0) {
    return true;
  }
  return false;
}

/**
 * Does the declared intent make the CONNECTIVITY posture decision-critical?
 *
 * CONNECTIVITY-ADVISORY (2026-08-25): while `CONNECTIVITY_GATES_GO` is false
 * this predicate no longer promotes anything into the GO gate — it gates the
 * OPTIONAL Connectivity side quest instead (`stepsFromConnectivityGaps` in
 * `nextStep.ts`), so the same declared-intent signals that used to require the
 * study now merely suggest it. The signal rules are unchanged and re-promotion
 * reuses them verbatim.
 *
 * Mirrors `intentCriticalScope`'s existing `interconnection` branches exactly,
 * promoting the SAME signals from 'intent-relevant' to 'required' — so the GO
 * gate stays a superset-consistent view of the platform's own relevance model
 * (the invariant in this module's header) rather than a second opinion:
 *
 *   - an ACTIVE interconnection queue position (`pre-application` / `queued` /
 *     `study`) — the grid energization path and the fiber path are being
 *     committed on the same timeline, and connectivity redundancy has to be
 *     designed against the declared uptime, not retrofitted;
 *   - `nuclearInterest` primary/considering — an SMR-backed island-capable
 *     build cannot lean on grid-adjacent carrier presence;
 *   - `priority` speed or resilience — both are DIRECT claims about uptime and
 *     time-to-service, which is precisely what route diversity underwrites.
 *
 * `queueStatus: 'none' | 'executed-LGIA'` / absent, and an undecided priority,
 * are NOT signals → not connectivity-required.
 */
export function intentSignalsConnectivityDiligence(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  const q = intent.interconnectionStatus?.queueStatus;
  if (q === 'pre-application' || q === 'queued' || q === 'study') return true;
  if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') return true;
  return intent.priority === 'speed' || intent.priority === 'resilience';
}

/**
 * Does the declared intent make PROCUREMENT exposure decision-critical?
 *
 * Mirrors `intentCriticalScope`'s `supply-chain-risk` branches, plus one
 * additional signal the scope model does not carry:
 *
 *   - `fuelAccess.supply` 'lateral-required' / 'none' — a single-path, long-lead
 *     fuel dependency is the classic BTM COD risk;
 *   - `nuclearInterest` primary/considering — SMR supply chains are the longest
 *     lead times in the catalogue;
 *   - a MULTI-PHASE build (`phases.length > 1`) — phased energization is a
 *     commitment to dates, and a date commitment is what turns equipment lead
 *     time from a cost into a risk. A single-phase or unphased project has no
 *     inter-phase schedule to protect.
 *
 * A firm-contracted or near-pipeline fuel supply on an unphased project is NOT
 * a signal → not supply-chain-required.
 */
/**
 * Has the facility load been declared? Power Availability's firm-capacity
 * assessment is measured against it, so without a load the report has no
 * denominator to assess adequacy against.
 *
 * Resolved through `resolveUltimateCampusMW`, the SSOT for "how big is this
 * project, one number" — NOT by reading `powerRequirementMW` directly. A
 * PHASED intent declares its load as `phases[].capacityMW` and legitimately
 * carries no top-level MW: `reconcilePowerRequirementWithPhases` treats an
 * unset value as "deliberately cleared — honored, never resurrected".
 *
 * Reading the scalar made such a site declare no load, which dropped
 * `power-availability` from the required set — and because
 * `buildExecutiveDecisionRollup` builds `perSourceVerdicts` over the MANDATORY
 * types only, a RED Power Availability report on that site got no row at all:
 * no PATH 2 floor, no PATH 6 critical cap. `computeSourceFloor` also saw it as
 * non-mandatory and therefore mitigable, capping at CONDITIONAL instead of
 * NO-GO. A 300 MW campus declared across phases, with a power report saying
 * there is no firm capacity until 2035, published two bands better than the
 * identical site that happened to type its load into one field.
 *
 * A pure range BUCKET still reads as undeclared: it carries no phases and no
 * scalar, and `powerStack` deliberately nulls every layer for it, so there is
 * genuinely no denominator to assess against.
 */
export function intentDeclaresLoad(intent: ProjectIntent | undefined): boolean {
  const mw = resolveUltimateCampusMW(intent);
  return typeof mw === 'number' && Number.isFinite(mw) && mw > 0;
}

export function intentSignalsSupplyChainDiligence(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  const supply = intent.fuelAccess?.supply;
  if (supply === 'lateral-required' || supply === 'none') return true;
  if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') return true;
  return (intent.phases?.length ?? 0) > 1;
}

/**
 * Does the declared intent create COMMUNITY exposure the project must answer?
 *
 * 2026-09-30 (owner: Public Sentiment is an ADDITIONAL analysis): this
 * predicate NO LONGER decides required-set membership — Public Sentiment is
 * never required. It is kept, and exported, as the relevance model's statement
 * of when community stance is a decision-DRIVING question (it stays
 * falsifiable by test), the same way `intentSignalsWaterResource` was kept
 * when water stopped being intent-gated.
 *
 * Public Sentiment was the one promotion with no existing `intentCriticalScope`
 * branch, so the rule is stated fresh and gated on DECLARED, externally-visible
 * impacts — the same axes the PSR's theme deep-dives cover:
 *
 *   - `priority === 'sustainability'` — the operator has made an environmental
 *     claim publicly, which is exactly the claim communities test;
 *   - an ON-SITE COMBUSTION build (a declared gas fuel supply) — noise, air
 *     emissions and fuel logistics are the three most common objection themes;
 *   - a WATER-WITHDRAWING heat-rejection sink — consumptive water use is the
 *     other dominant objection theme, and this reads the SAME
 *     `WATER_WITHDRAWING_SINKS` set as the water gate so the two cannot drift.
 *
 * A grid-only, dry-cooled project with no sustainability claim has no declared
 * community-facing exposure → not sentiment-required. Absence of a declared
 * impact is NOT evidence of community support; it is an absence of the signal
 * that would make the diligence unambiguously relevant, which is the standard
 * this whole gate is held to.
 */
export function intentSignalsCommunityExposure(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  if (intent.priority === 'sustainability') return true;
  const supply = intent.fuelAccess?.supply;
  if (supply === 'near-pipeline' || supply === 'lateral-required' || supply === 'firm-contracted') {
    return true;
  }
  const sink = intent.bopHeatRejection;
  return sink != null && WATER_WITHDRAWING_SINKS.has(sink);
}

/**
 * The EXPLICIT required-report set for a GO decision, ordered (source reports
 * before executive-summary). Always includes feasibility and water-resource
 * (`ALWAYS_REQUIRED_FOR_GO`); power-availability is the scenario-gated
 * Generation & Impact report and joins the moment a load is declared unless the
 * site DECLARES grid-only. Adds the remaining conditional domains per the
 * documented intent + authority rules.
 *
 * NB: executive-summary is NOT auto-added here — it is a synthesis deliverable
 * the wizard offers once the source reports exist. The GO gate is satisfied by
 * the SOURCE reports; the exec summary is the user-facing capstone. If a
 * deployment wants the exec summary mandatory, it is already present in
 * `GO_REPORT_ORDER` and can be appended; we keep the gate to source reports so
 * it stays falsifiable and not tautological.
 */
export function requiredReportsForGo(
  intent: ProjectIntent | undefined,
  powerAvailabilityPreferences?: PowerAvailabilityPreferences,
  site?: RequirementAuthoritySite,
): ReportTypeId[] {
  return requiredReportsForGoUnderPolicy(
    intent,
    powerAvailabilityPreferences,
    CONNECTIVITY_GATES_GO,
    site,
  );
}

/**
 * The required-set rule parameterized on the connectivity policy — the TEST
 * SEAM for `CONNECTIVITY_GATES_GO`. Production always goes through
 * `requiredReportsForGo` (which binds the constant); tests call this directly
 * to exercise BOTH the demoted and the re-promoted branch, so the flip stays
 * a proven one-line change rather than a hopeful one.
 */
export function requiredReportsForGoUnderPolicy(
  intent: ProjectIntent | undefined,
  powerAvailabilityPreferences: PowerAvailabilityPreferences | undefined,
  connectivityGatesGo: boolean,
  site?: RequirementAuthoritySite,
): ReportTypeId[] {
  const set = new Set<ReportTypeId>(ALWAYS_REQUIRED_FOR_GO);
  if (requiresGenerationImpactTestFit({
    projectIntent: intent,
    powerAvailabilityPreferences,
  })) {
    set.add('test-fit');
  }
  // Power Availability — authority-gated on the declared facility load. Every
  // data centre needs power, so no separate intent signal is required: the
  // moment a load exists the assessment is part of the decision basis.
  //
  // Read from INTENT, not from `site`. `site` is an OPTIONAL parameter, so
  // gating on it would silently drop Power from the required set for any caller
  // that omits it — the exact shape of hazard where an optional argument
  // quietly weakens the decision gate. `resolveAnalysisDecisionMateriality`
  // reads the same field via `site.projectIntent`, so the two agree.
  // A GRID-ONLY site does not require Generation & Impact — and as of Stage 1.5
  // that is finally SAFE to say here.
  //
  // HISTORY, because this line was changed, reverted, and changed back, and the
  // ordering is the whole point. Gating this on applicability was reverted on
  // 2026-09-05 after an adversarial review: `perSourceVerdicts` was built over
  // `mandatory` ONLY, so the required set was also what gave a report authority
  // to FLOOR the call. Dropping power for a grid-only site therefore stopped a
  // RED power report flooring to NO-GO — a site could escape a red source just
  // by declaring grid-only, verified as PATH 2 returning 'go'.
  //
  // Stage 1.5 (567ea2e57) removed that coupling: the rollup now builds a row for
  // every PRESENT non-advisory source and caps a non-mandatory one at
  // CONDITIONAL. A report can now lose required status WITHOUT losing its
  // floor, which is exactly the precondition the revert note asked for. So a red
  // power report on a grid-only site still binds the call — it caps rather than
  // declines, which is the correct semantics for a report that does not apply to
  // the site at all.
  //
  // Only 'generation-not-applicable' drops it. `decisionMaterial` is false for
  // BOTH grid-only AND merely-unpromoted, and a BTM site that has not promoted a
  // scenario YET must stay required — otherwise the GO gate stops asking for a
  // report the site can and should produce.
  //
  // And 'generation-not-applicable' fires only for a DECLARED grid-only
  // strategy (P1.10). `assessGenerationImpactTestFitApplicability` used to
  // infer grid-only from phases coded `powerSource: 'grid'` with the strategy
  // still undeclared, which dropped the requirement here — and because
  // `computeSourceFloor` derives "mitigable" from this same set, a RED
  // Generation & Impact report on an undeclared site (the default state) fell
  // from NO-GO to CONDITIONAL on every portfolio surface. Such a site now reads
  // `undecided`, keeps the requirement, and is told to declare.
  //
  // `site`-absent stays STRICT for the reason the original comment gives above:
  // a smaller required set can only make GO easier, and an omitted optional
  // argument must never weaken the gate. Both production callers pass one.
  if (intentDeclaresLoad(intent)) {
    const neverApplicable = site != null
      && resolveAnalysisDecisionMateriality('power-availability', site, intent).reasonCode
        === 'generation-not-applicable';
    if (!neverApplicable) set.add('power-availability');
  }
  // environmental is unconditional as of the two-stage pipeline (Phase 3, D1);
  // `intentSignalsEnvironmental` no longer gates membership (it still widens
  // `intentCriticalScope`'s evidence domains).
  // water-resource is RETIRED (Phase 3 part B): its screen is part of the
  // feasibility report, so it is never required and never added here.
  // Intent-gated connectivity / procurement / community requirements. SCM is
  // additionally authority-gated on a reportable user-owned equipment basis.
  // a MITIGABLE floor source (see sourceFloorPolicy): being required means a
  // missing or bad reading caps the call at CONDITIONAL, never NO-GO.
  //
  // CONNECTIVITY-ADVISORY (2026-08-25): connectivity is additionally gated on
  // the policy flag — with `CONNECTIVITY_GATES_GO` false it is NEVER required,
  // so it stops feeding PATH 2/4/6 entirely and surfaces only as an advisory
  // row + optional side quest. See the flag's re-promotion checklist.
  if (connectivityGatesGo && intentSignalsConnectivityDiligence(intent)) {
    set.add('interconnection');
  }
  // Supply Chain Risk — Stage 2 · Mature validation for every site with an
  // on-site power path (two-stage Phase 3, D2). It is REQUIRED before the
  // equipment basis exists (the plan lanes it "Choose equipment first"), so
  // the decision waits for the sourcing question instead of skipping it; the
  // server chokepoint still refuses to RUN it without a reportable BOM. A
  // declared grid-only site has no generation equipment to source.
  const generationNeverApplies = site != null
    && resolveAnalysisDecisionMateriality('power-availability', site, intent).reasonCode
      === 'generation-not-applicable';
  if (!generationNeverApplies) set.add('supply-chain-risk');
  // public-sentiment is NEVER added (owner, 2026-09-30): it is an additional
  // analysis outside both stages. Direction: GO gets EASIER for a site whose
  // intent signalled community exposure — its decision no longer waits for a
  // sentiment report, and a missing or stale one no longer caps it (a present
  // red one still caps at CONDITIONAL as a non-mandatory source).
  // financial is unconditional as of the two-stage pipeline (Phase 3, D2):
  // Stage 2 · Mature validation for every site. FIN-1's authority gate now
  // lives on RUNNING the report (`resolveAnalysisDecisionMateriality` +
  // the enqueue chokepoint), not on whether the decision asks for it.
  // Emit in the stable GO order (source reports before executive-summary).
  return GO_REPORT_ORDER.filter((t) => set.has(t));
}

/**
 * Analysis-first name for the GO evidence set. Report types remain the stable
 * domain identifiers, so legacy storage and decision records stay compatible.
 */
export function requiredAnalysesForGo(
  intent: ProjectIntent | undefined,
  powerAvailabilityPreferences?: PowerAvailabilityPreferences,
  site?: RequirementAuthoritySite,
): ReportTypeId[] {
  return requiredReportsForGo(intent, powerAvailabilityPreferences, site);
}

/**
 * Is `type` part of the GO gate for the declared intent? Convenience predicate
 * for the wizard's per-report "required for GO" badge.
 */
export function isReportRequiredForGo(
  type: ReportTypeId,
  intent: ProjectIntent | undefined,
  powerAvailabilityPreferences?: PowerAvailabilityPreferences,
  site?: RequirementAuthoritySite,
): boolean {
  return requiredReportsForGo(intent, powerAvailabilityPreferences, site).includes(type);
}

/** Analysis-first predicate; preserves `isReportRequiredForGo` for callers. */
export function isAnalysisRequiredForGo(
  type: ReportTypeId,
  intent: ProjectIntent | undefined,
  powerAvailabilityPreferences?: PowerAvailabilityPreferences,
  site?: RequirementAuthoritySite,
): boolean {
  return isReportRequiredForGo(type, intent, powerAvailabilityPreferences, site);
}

/**
 * The full stable GO order (the universe of GO-gate report types). Exposed so
 * the wizard can order a SUPERSET (required ∪ scope) consistently — required
 * types follow this order; any extra scope types append after.
 */
export const GO_REPORT_TYPE_ORDER: readonly ReportTypeId[] = GO_REPORT_ORDER;
/** Analysis-first alias over the same stable domain order. */
export const GO_ANALYSIS_TYPE_ORDER: readonly ReportTypeId[] = GO_REPORT_TYPE_ORDER;

/**
 * Order an arbitrary report-type list by the canonical GO order, with any
 * types not in the GO order appended in their original relative order. Used by
 * the orchestrator to keep `recommendNext` stable.
 */
export function orderByGoSequence(types: readonly ReportTypeId[]): ReportTypeId[] {
  const inGo = GO_REPORT_ORDER.filter((t) => types.includes(t));
  const rest = types.filter((t) => !GO_REPORT_ORDER.includes(t));
  return [...inGo, ...rest];
}

/** Analysis-first ordering helper; preserves stable legacy report ordering. */
export function orderAnalysesByGoSequence(types: readonly ReportTypeId[]): ReportTypeId[] {
  return orderByGoSequence(types);
}
